import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import type { ViewResult } from '../../../views/result.js';
import type { ViewSpec } from '../../../views/spec.js';
import { runViewSpec } from '../../views/api/views.client.js';
import type { AgentWidgetListItem } from '../agent-widgets.js';
import {
  deleteAgentWidget,
  listAgentWidgets,
  updateAgentWidgetSpec,
} from '../api/agent-widgets.client.js';

export const AGENT_WIDGETS_KEY = ['dashboard', 'agent-widgets'] as const;

export function useAgentWidgets(): UseQueryResult<AgentWidgetListItem[]> {
  return useQuery({ queryKey: AGENT_WIDGETS_KEY, queryFn: listAgentWidgets });
}

export function useAgentWidgetData(
  widgetId: string,
  spec: ViewSpec | undefined,
): UseQueryResult<ViewResult> {
  return useQuery({
    // A widget id can retain a new spec after a block is removed. Including the
    // spec prevents the old rendered data from being reused under the same id.
    queryKey: [...AGENT_WIDGETS_KEY, widgetId, 'data', spec],
    queryFn: () => runViewSpec(spec as ViewSpec),
    enabled: spec !== undefined,
  });
}

export function useDeleteAgentWidget(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAgentWidget,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: AGENT_WIDGETS_KEY }),
  });
}

export interface UpdateAgentWidgetSpecVariables {
  readonly id: string;
  readonly spec: ViewSpec;
  readonly removedBlockIndex: number;
}

export function useUpdateAgentWidgetSpec(): UseMutationResult<
  void,
  Error,
  UpdateAgentWidgetSpecVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, spec }) => updateAgentWidgetSpec(id, spec),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: AGENT_WIDGETS_KEY }),
  });
}
